import type { LucideIcon } from 'lucide-react';
import { Check, FileText, Headphones } from 'lucide-react';
import { STRINGS } from '@/strings/strings';
import { cn } from '@/lib/utils';

interface MediaOptionsGroupProps {
  pdfActive: boolean;
  audioActive: boolean;
  onPdfChange: (active: boolean) => void;
  onAudioChange: (active: boolean) => void;
  /** Override the default `border-dream-gold` on the selected-state
   *  border (e.g. `border-dream-navy` on SB so gold stays reserved for
   *  the primary "Geschichte erstellen" CTA). */
  selectedBorderClassName?: string;
}

export function MediaOptionsGroup({
  pdfActive,
  audioActive,
  onPdfChange,
  onAudioChange,
  selectedBorderClassName,
}: MediaOptionsGroupProps) {
  return (
    <div className="flex flex-col gap-3">
      <MediaToggleButton
        active={pdfActive}
        onToggle={() => onPdfChange(!pdfActive)}
        label={STRINGS.builder.shared.pdfToggleLabel}
        Icon={FileText}
        selectedBorderClassName={selectedBorderClassName}
      />
      <MediaToggleButton
        active={audioActive}
        onToggle={() => onAudioChange(!audioActive)}
        label={STRINGS.builder.shared.audioToggleLabel}
        Icon={Headphones}
        selectedBorderClassName={selectedBorderClassName}
      />
    </div>
  );
}

interface MediaToggleButtonProps {
  active: boolean;
  onToggle: () => void;
  label: string;
  Icon: LucideIcon;
  // Required in the inner-component contract (caller may pass undefined).
  // Avoids `exactOptionalPropertyTypes` complaints when the optional prop
  // on the parent flows through to a child that declares it as `?: string`.
  selectedBorderClassName: string | undefined;
}

function MediaToggleButton({
  active,
  onToggle,
  label,
  Icon,
  selectedBorderClassName,
}: MediaToggleButtonProps) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={active}
      className={cn(
        'flex h-12 w-full items-center gap-3 rounded-lg px-3 text-left text-base font-medium text-dream-navy transition-colors',
        active
          ? 'border-2 border-dream-gold bg-dream-white'
          : 'border-[1.5px] border-dream-navy/10 bg-dream-white/80',
        active && selectedBorderClassName,
      )}
    >
      <Icon aria-hidden className="h-5 w-5 shrink-0 text-dream-navy" />
      <span className="flex-1">{label}</span>
      <Check
        aria-hidden
        strokeWidth={3}
        className={cn('h-5 w-5 shrink-0 text-success', !active && 'invisible')}
      />
    </button>
  );
}
